/**
 * InstitutionalTab - Institutional holdings table
 */
import { useInstitutionalHoldings, useInstitutionalSummary } from '../../hooks/useResearch'
import type { InstitutionalHoldingListItem } from '../../types/research'

interface InstitutionalTabProps {
  symbol: string
}

function SummaryStat({
  label,
  value,
  subValue,
}: {
  label: string
  value: string | number
  subValue?: string
}) {
  return (
    <div className="bg-gray-50 rounded-lg p-4 text-center">
      <p className="text-sm text-gray-500 font-medium">{label}</p>
      <p className="text-2xl font-semibold text-gray-900">{value}</p>
      {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div className="animate-pulse">
        {/* Header */}
        <div className="bg-gray-50 px-4 py-3 flex gap-4">
          <div className="h-4 w-32 bg-gray-200 rounded" />
          <div className="flex-1" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-20 bg-gray-200 rounded" />
          <div className="h-4 w-16 bg-gray-200 rounded" />
        </div>
        {/* Rows */}
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="px-4 py-4 flex gap-4 border-t border-gray-200">
            <div className="h-4 w-40 bg-gray-100 rounded" />
            <div className="flex-1" />
            <div className="h-4 w-20 bg-gray-100 rounded" />
            <div className="h-4 w-20 bg-gray-100 rounded" />
            <div className="h-4 w-16 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  )
}

function formatShares(shares: number): string {
  if (shares >= 1_000_000_000) {
    return `${(shares / 1_000_000_000).toFixed(2)}B`
  }
  if (shares >= 1_000_000) {
    return `${(shares / 1_000_000).toFixed(2)}M`
  }
  if (shares >= 1_000) {
    return `${(shares / 1_000).toFixed(1)}K`
  }
  return shares.toLocaleString()
}

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '--'
  const absValue = Math.abs(value)
  if (absValue >= 1_000_000_000) {
    return `$${(absValue / 1_000_000_000).toFixed(2)}B`
  }
  if (absValue >= 1_000_000) {
    return `$${(absValue / 1_000_000).toFixed(2)}M`
  }
  if (absValue >= 1_000) {
    return `$${(absValue / 1_000).toFixed(1)}K`
  }
  return `$${absValue.toLocaleString()}`
}

function getChangeDisplay(holding: InstitutionalHoldingListItem): {
  text: string
  color: string
} {
  if (holding.is_new_position) {
    return { text: 'NEW', color: 'text-green-600 bg-green-100' }
  }
  if (holding.is_closed_position) {
    return { text: 'CLOSED', color: 'text-red-600 bg-red-100' }
  }

  const changePct = holding.shares_change_pct
  if (changePct === null || changePct === undefined) {
    return { text: '--', color: 'text-gray-400' }
  }

  if (changePct > 0) {
    return {
      text: `+${(changePct * 100).toFixed(1)}%`,
      color: 'text-green-600',
    }
  }
  if (changePct < 0) {
    return {
      text: `${(changePct * 100).toFixed(1)}%`,
      color: 'text-red-600',
    }
  }
  return { text: '0%', color: 'text-gray-500' }
}

export function InstitutionalTab({ symbol }: InstitutionalTabProps) {
  const {
    data: summary,
    isLoading: summaryLoading,
  } = useInstitutionalSummary(symbol)

  const {
    data: holdingsData,
    isLoading: holdingsLoading,
    error,
  } = useInstitutionalHoldings(symbol, { limit: 100 })

  const holdings = holdingsData?.holdings ?? []
  const isLoading = summaryLoading || holdingsLoading

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      {summaryLoading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-4 animate-pulse">
              <div className="h-4 w-24 bg-gray-200 rounded mx-auto mb-2" />
              <div className="h-8 w-20 bg-gray-200 rounded mx-auto" />
            </div>
          ))}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryStat
            label="Total Holders"
            value={summary.total_institutions.toLocaleString()}
          />
          <SummaryStat
            label="Shares Held"
            value={formatShares(summary.total_shares)}
          />
          <SummaryStat
            label="Market Value"
            value={formatCurrency(summary.total_market_value)}
          />
          <SummaryStat
            label="Period"
            value={new Date(summary.report_period).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric',
            })}
            subValue={`${summary.new_positions} new, ${summary.closed_positions} closed`}
          />
        </div>
      ) : null}

      {/* Error state */}
      {error && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-600">
          No institutional holdings data available for this symbol.
        </div>
      )}

      {/* Loading state for table */}
      {holdingsLoading && <TableSkeleton />}

      {/* Holdings table */}
      {!isLoading && !error && (
        <>
          {holdings.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Investor Name
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Shares
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Value
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Change
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Change %
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {holdings.map((holding) => {
                      const changeDisplay = getChangeDisplay(holding)
                      const isSpecialStatus =
                        holding.is_new_position || holding.is_closed_position

                      return (
                        <tr
                          key={holding.id}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-4 text-sm font-medium text-gray-900">
                            <div className="flex items-center gap-2">
                              <span className="truncate max-w-xs">
                                {holding.investor_name}
                              </span>
                              {isSpecialStatus && (
                                <span
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${changeDisplay.color}`}
                                >
                                  {changeDisplay.text}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 text-right whitespace-nowrap">
                            {formatShares(holding.shares)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600 text-right whitespace-nowrap">
                            {formatCurrency(holding.market_value)}
                          </td>
                          <td className="px-4 py-4 text-sm text-right whitespace-nowrap">
                            {holding.shares_change !== null &&
                            holding.shares_change !== undefined ? (
                              <span
                                className={
                                  holding.shares_change > 0
                                    ? 'text-green-600'
                                    : holding.shares_change < 0
                                      ? 'text-red-600'
                                      : 'text-gray-500'
                                }
                              >
                                {holding.shares_change > 0 ? '+' : ''}
                                {formatShares(holding.shares_change)}
                              </span>
                            ) : (
                              <span className="text-gray-400">--</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm text-right whitespace-nowrap">
                            {!isSpecialStatus && (
                              <span className={changeDisplay.color}>
                                {changeDisplay.text}
                              </span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <h3 className="mt-4 text-sm font-medium text-gray-900">
                No institutional holdings
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                No institutional ownership data is available for this company.
              </p>
            </div>
          )}

          {/* Results count */}
          {holdings.length > 0 && (
            <p className="text-sm text-gray-500 text-center">
              Showing {holdings.length} institutional holder
              {holdings.length !== 1 ? 's' : ''}
            </p>
          )}
        </>
      )}
    </div>
  )
}

export default InstitutionalTab
